import { useLayoutEffect, useRef } from "react";

interface VideoOptions {
	url: string;
	className: string;
	play: boolean;
	priority: number;
	onReady?: () => void;
	onPlaying?: () => void;
	onFailed?: () => void;
}
interface Lease {
	host: HTMLElement;
	options: { current: VideoOptions };
	ready: boolean;
	playing: boolean;
}
interface Resource {
	video: HTMLVideoElement;
	leases: Set<Lease>;
	owner?: Lease;
	frameReady: boolean;
	expiry?: ReturnType<typeof setTimeout>;
}

// Retain the decoder and buffered bytes during inline → hover → inline handoffs.
// Idle clips expire quickly; even rapid browsing cannot cache the whole catalogue.
const resources = new Map<string, Resource>();
const CACHE_LIMIT = 12;
const IDLE_TTL = 15_000;

function dispose(url: string, resource: Resource) {
	clearTimeout(resource.expiry);
	resource.video.pause();
	resource.video.removeAttribute("src");
	resource.video.load();
	resource.video.remove();
	resources.delete(url);
}

function trim() {
	for (const [url, resource] of resources) {
		if (resources.size <= CACHE_LIMIT) break;
		if (!resource.leases.size) dispose(url, resource);
	}
}

function notifyReady(resource: Resource) {
	const owner = resource.owner;
	if (owner && !owner.ready && resource.video.readyState >= 3) {
		owner.ready = true;
		owner.options.current.onReady?.();
	}
}

function notifyPlaying(resource: Resource) {
	const owner = resource.owner;
	if (!owner?.options.current.play || resource.video.paused) return;
	if (!owner.playing) {
		owner.playing = true;
		owner.options.current.onPlaying?.();
	}
	const reveal = () => {
		resource.frameReady = true;
		resource.video.dataset.playing = String(!!resource.owner?.options.current.play && !resource.video.paused);
	};
	if (resource.frameReady || !resource.video.requestVideoFrameCallback) reveal();
	else resource.video.requestVideoFrameCallback(reveal);
}

function sync(resource: Resource) {
	const owner = [...resource.leases].sort((a, b) => b.options.current.priority - a.options.current.priority).at(0);
	resource.owner = owner;
	const video = resource.video;
	if (!owner) { video.pause(); video.remove(); return; }
	if (video.parentElement !== owner.host) owner.host.append(video);
	video.className = owner.options.current.className;
	video.dataset.playing = String(owner.options.current.play && resource.frameReady);
	notifyReady(resource);
	if (video.error) { owner.options.current.onFailed?.(); return; }
	if (!owner.options.current.play) { owner.playing = false; video.pause(); return; }
	if (video.paused) {
		void video.play().then(() => { if (resource.owner === owner) notifyPlaying(resource); }).catch((error: unknown) => {
			// A transfer, leave, or unmount may legitimately interrupt play().
			if (error instanceof DOMException && error.name === "AbortError") return;
			if (resource.owner === owner && owner.options.current.play) owner.options.current.onFailed?.();
		});
	} else if (video.readyState >= 2) notifyPlaying(resource);
}

function acquire(url: string, lease: Lease) {
	let resource = resources.get(url);
	if (!resource) {
		const video = document.createElement("video");
		video.muted = true; video.loop = true; video.playsInline = true; video.preload = "auto";
		video.setAttribute("aria-hidden", "true");
		const entry: Resource = { video, leases: new Set(), frameReady: false };
		video.addEventListener("canplay", () => notifyReady(entry));
		video.addEventListener("playing", () => notifyPlaying(entry));
		video.addEventListener("error", () => entry.owner?.options.current.onFailed?.());
		resource = entry;
		resources.set(url, resource);
		video.src = url;
	}
	clearTimeout(resource.expiry);
	resource.leases.add(lease);
	sync(resource);
	trim();
	return resource;
}

function release(url: string, resource: Resource, lease: Lease) {
	resource.leases.delete(lease);
	sync(resource);
	if (resource.leases.size) return;
	if (resource.video.error) { dispose(url, resource); return; }
	// Most recently used idle clips are evicted last.
	resources.delete(url); resources.set(url, resource);
	resource.expiry = setTimeout(() => dispose(url, resource), IDLE_TTL);
	trim();
}

/** The host is React-owned; the pooled video can safely move between hosts. */
export function PreviewVideo(options: VideoOptions) {
	const host = useRef<HTMLSpanElement>(null);
	const current = useRef(options);
	current.current = options;
	const resource = useRef<Resource | null>(null);
	useLayoutEffect(() => {
		const lease: Lease = { host: host.current!, options: current, ready: false, playing: false };
		const entry = acquire(options.url, lease);
		resource.current = entry;
		return () => { release(options.url, entry, lease); resource.current = null; };
	}, [options.url]);
	useLayoutEffect(() => { if (resource.current) sync(resource.current); });
	return <span ref={host} className="preview-video-host" aria-hidden="true" />;
}
